import { isAddress } from "viem";
import { z } from "zod";
import {
  executionOwnerWithdrawalCreateSchema,
  executionOwnerWithdrawalPreviewInputSchema,
  MAX_EXECUTION_OPERATION_ACTIONS,
} from "./requests.js";
import { lazySchema } from "@repo/lazy-schema";

export const MAX_OWNER_USDC_WITHDRAWAL_ASSETS = MAX_EXECUTION_OPERATION_ACTIONS;

const ownerUsdcExpectedOwnerAddress = lazySchema(() =>
  z
    .string()
    .refine(
      (value) => isAddress(value) && !/^0x0{40}$/iu.test(value),
      "A nonzero EVM owner address is required",
    )
    .transform((value) => value.toLowerCase()),
);

const refineOwnerUsdcSelection = (
  input: { readonly assets: readonly { readonly assetId: string }[] },
  ctx: z.RefinementCtx,
): void => {
  for (const [index, asset] of input.assets.entries()) {
    if (!/^eip155:[1-9]\d*\/erc20:0x[0-9a-f]{40}$/u.test(asset.assetId)) {
      ctx.addIssue({
        code: "custom",
        path: ["assets", index, "assetId"],
        message: "Owner USDC withdrawals require canonical ERC-20 selections",
      });
    }
  }
};

/** Exact selected ERC-20 inputs. The engine owns recipient, cash identity and routing. */
export const executionOwnerUsdcWithdrawalCreateSchema = lazySchema(() =>
  executionOwnerWithdrawalCreateSchema
    .safeExtend({ expectedOwnerAddress: ownerUsdcExpectedOwnerAddress })
    .superRefine(refineOwnerUsdcSelection),
);

/** What an owner USDC withdrawal would repay, before it is requested. */
export const executionOwnerUsdcWithdrawalPreviewInputSchema = lazySchema(() =>
  executionOwnerWithdrawalPreviewInputSchema
    .safeExtend({ expectedOwnerAddress: ownerUsdcExpectedOwnerAddress })
    .superRefine(refineOwnerUsdcSelection),
);

export type ExecutionOwnerUsdcWithdrawalPreviewInput = z.infer<
  typeof executionOwnerUsdcWithdrawalPreviewInputSchema
>;

/**
 * Runtime actions an owner USDC withdrawal compiles to: every routed conversion
 * action, one `deliver_proceeds` per account with conversions, and one exact
 * transfer batch per account with selected existing USDC. The client counts each
 * conversion as one action before submission; the engine counts routed actions
 * and stays authoritative against MAX_EXECUTION_OPERATION_ACTIONS.
 */
export const countOwnerUsdcWithdrawalActions = (
  selections: readonly {
    accountRef: string;
    /** Routed venue actions for this selection; 0 for selected existing USDC. */
    conversionActions: number;
  }[],
): number => {
  const convertingAccounts = new Set<string>();
  const cashAccounts = new Set<string>();
  let conversionActions = 0;
  for (const selection of selections) {
    if (selection.conversionActions > 0) {
      conversionActions += selection.conversionActions;
      convertingAccounts.add(selection.accountRef);
    } else cashAccounts.add(selection.accountRef);
  }
  return conversionActions + convertingAccounts.size + cashAccounts.size;
};

export type ExecutionOwnerUsdcWithdrawalCreateInput = z.infer<
  typeof executionOwnerUsdcWithdrawalCreateSchema
>;
